import { useSuspenseQuery } from "@apollo/client";
import { type FallbackProps } from "react-error-boundary";
import { useBookingMetadataDisplay } from "./hooks";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { InfoField } from "@/components/InfoField";
import { Heading3 } from "@/components/Typography";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql, type ResultOf } from "@/graphql";
import { getErrorMessage } from "@/lib/utils/errors";

export type MetadataBooking = NonNullable<ResultOf<typeof supplyChainBookingMetadata>["supplyChainBooking"]>;

export function BookingMetadata({ bookingId }: { bookingId: string }) {
  const { data } = useSuspenseQuery(supplyChainBookingMetadata, { variables: { id: bookingId } });

  const booking = data.supplyChainBooking;

  const { sections, hasVisibleFields } = useBookingMetadataDisplay(booking);

  return (
    <>
      <article>
        <Card className="p-4 flex min-h-64 flex-col flex-1 gap-4">
          <header className="flex justify-between items-center">
            <CardTitle className="font-semibold text-xl">Additional Info</CardTitle>
          </header>
          <Card className="p-4 bg-muted-light flex flex-col flex-grow">
            {hasVisibleFields ? (
              <div className="flex flex-col gap-6">
                {sections.map((section) => (
                  <div key={section.id} className="flex flex-col gap-4">
                    <Heading3 className="text-sm text-foreground/80 pb-1">{section.title}</Heading3>
                    <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {section.items.map(({ id, label, value, fullWidth }) => (
                        <InfoField key={id} fullWidth={fullWidth}>
                          <CardDescription className="text-foreground/80">{label}</CardDescription>
                          <CardTitle
                            className="truncate leading-relaxed"
                            title={typeof value === "string" ? value : undefined}
                          >
                            {value}
                          </CardTitle>
                        </InfoField>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No additional information available"
                description="No additional information has been configured for display."
                animationPath="/animations/searching.json"
                size={250}
              />
            )}
          </Card>
        </Card>
      </article>
    </>
  );
}

export function BookingMetadataSkeleton() {
  // Create two sections with 3 fields each for the skeleton
  const sections = [1, 2];
  const fieldsPerSection = 3;

  return (
    <Card className="p-4 flex min-h-64 flex-col flex-1 gap-4">
      <div className="flex justify-between items-center h-10">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-9 w-24" />
      </div>
      <Card className="p-4 bg-muted-light flex flex-col flex-grow">
        <div className="flex flex-col gap-6">
          {sections.map((sectionIndex) => (
            <div key={sectionIndex} className="flex flex-col gap-4">
              {/* Section title skeleton */}
              <Skeleton className="h-5 w-48" />
              <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                <div role="status" aria-busy="true" aria-label="Loading additional info" className="contents">
                  {[...Array(fieldsPerSection).keys()].map((fieldIndex) => {
                    const uniqueKey = `${sectionIndex}-${fieldIndex}`;
                    return (
                      <InfoField key={uniqueKey}>
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-5 w-32" />
                      </InfoField>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </Card>
  );
}

export function BookingMetadataError({ error }: FallbackProps) {
  return (
    <Card className="p-4 flex min-h-64 flex-col flex-1 gap-4">
      <div className="flex justify-between items-center">
        <CardTitle className="font-semibold text-xl">Additional Info</CardTitle>
      </div>
      <ErrorCard title="Unable to load additional info" description={getErrorMessage(error)} />
    </Card>
  );
}

export const supplyChainBookingMetadata = graphql(`
  query SupplyChainBookingMetadata($id: UUID!) {
    supplyChainBooking(id: $id) {
      id
      lotNumber
      sexClass
      confirmed
      accreditations {
        id
        status
        label
      }
      price
      penNumber
      sourceNote
      program {
        id
        label
      }
      grid {
        id
        label
      }
      penNumber
      price
      truckingCompany {
        id
        businessName
      }
      estimatedWeightNote
      shippedWeight
      baseWeight
      veterinarian {
        id
        user {
          id
          firstName
          lastName
        }
      }
      brandNote
      serviceProvided
      certificateNumber
      freightCost
      dateOfSale
      dateOfVisit
      projectedOutDate
      broker {
        id
        businessName
      }
      purchasePricePerCwt
      slide
      shrinkPercent
      breedNote
      dateDelivered
      accountNumber
      riskManagement
      estimatedChoicePercent
      labels {
        id
        status
        label
      }
      condition
      conditions
      ownershipType
    }
  }
`);
